import { useEffect, useId, useState } from 'react';
import { App as CapApp } from '@capacitor/app';

import { begrenze, spanne } from '../../domain/helligkeit';
import { Helligkeit, liesSitzung, merkeSitzung } from '../../platform/helligkeit';
import { useT } from '../../i18n';
import { useAppStore } from '../../store/useAppStore';
import styles from './Helligkeit.module.css';

/**
 * Der Regler, mit dem der Benutzer die Helligkeit zwischen den Grenzen der Aufsicht wählt.
 *
 * ⚠️ **Er steht vor dem Schloss.** `app/App.tsx` entscheidet zwischen Sperrdialog und
 * Einstellungsdialog; diese Zeile steht **über** dieser Verzweigung und ist auch bei gesetztem
 * `settings_password` bedienbar. Sie ist die einzige Ausnahme von der Sperre: das Passwort hält
 * den Prüfer von den Prüfungseinstellungen fern, nicht von der Lesbarkeit seines Bildschirms.
 *
 * ⚠️ **Beim Aufgehen wird nichts gesetzt** (Entscheidung des Nutzers, 2026-09-17). Der Regler
 * zeigt, wo das Gerät gerade steht, auf die Spanne beschnitten. Geschrieben wird erst beim
 * ersten Griff — sonst verstellte jeder App-Start die systemweite Helligkeit, und
 * zurückgestellt wird beim Verlassen ausdrücklich nicht.
 *
 * ⚠️ **Eine Unschärfe bleibt:** steht das Gerät bei 5 % und die Untergrenze bei 30, zeigt der
 * Regler 30, während der Schirm auf 5 steht — die Spanne kann 5 nicht darstellen. Der erste
 * Griff rückt beides zusammen. Das ist der Preis dafür, beim Start nicht einzugreifen.
 */
export function HelligkeitZeile() {
  const t = useT();
  const id = useId();
  const min = useAppStore((s) => s.helligkeitMin);
  const max = useAppStore((s) => s.helligkeitMax);
  const rahmen = spanne(min, max);
  const [wert, setWert] = useState<number | null>(null);

  /**
   * Beim Aufgehen: den Stand der Sitzung nehmen, sonst den des Geräts — beides nur zum
   * Anzeigen, ohne zu schreiben.
   *
   * Die Sitzung gewinnt, weil sie die Absicht des Benutzers trägt; der Gerätewert ist nur der
   * Ausgangspunkt, solange niemand gewählt hat.
   */
  useEffect(() => {
    let lebt = true;
    const gemerkt = liesSitzung();
    if (gemerkt !== null) {
      setWert(begrenze(gemerkt, rahmen));
      return;
    }
    void Helligkeit.lies()
      .then((r) => {
        if (lebt) setWert(begrenze(r.wert, rahmen));
      })
      // `begrenze` macht aus NaN die Mitte — der Rückfall, wenn das Gerät nicht antwortet
      // (im Browser, oder wenn das Plugin nicht angemeldet ist).
      .catch(() => {
        if (lebt) setWert(begrenze(Number.NaN, rahmen));
      });
    return () => {
      lebt = false;
    };
  }, [rahmen.min, rahmen.max]);

  /**
   * Zurück aus dem Hintergrund: den gemerkten Wert erneut setzen.
   *
   * Zwischendurch kann jemand das Kontrollzentrum benutzt haben. Liegt nichts in der Sitzung,
   * hat der Benutzer den Regler nie angefasst — dann bleibt die Helligkeit unberührt.
   */
  useEffect(() => {
    const horcher = CapApp.addListener('appStateChange', ({ isActive }) => {
      const gemerkt = liesSitzung();
      if (!isActive || gemerkt === null) return;
      void Helligkeit.setze({ wert: begrenze(gemerkt, rahmen) });
    });
    return () => {
      void horcher.then((h) => h.remove());
    };
  }, [rahmen.min, rahmen.max]);

  const ziehe = (roh: number) => {
    const neu = begrenze(roh, rahmen);
    setWert(neu);
    merkeSitzung(neu);
    // Schon beim Ziehen, nicht erst beim Loslassen: im Gegenlicht tastet man sonst blind.
    void Helligkeit.setze({ wert: neu });
  };

  // Solange nicht bekannt ist, wo das Gerät steht, steht hier nichts — ein Regler, der von 100
  // auf den wahren Wert springt, sieht wie ein Fehler aus.
  if (wert === null) return null;

  if (rahmen.min === rahmen.max) {
    return (
      <div className={styles.zeile}>
        <span className={styles.label}>{t.settings.brightness}</span>
        <span className={styles.fest}>{rahmen.min} %</span>
        <span className={styles.note}>{t.settings.brightnessFixed}</span>
      </div>
    );
  }

  return (
    <div className={styles.zeile}>
      <label className={styles.label} htmlFor={id}>
        {t.settings.brightness}
      </label>
      <input
        id={id}
        className={styles.regler}
        type="range"
        min={rahmen.min}
        max={rahmen.max}
        step={1}
        value={wert}
        onChange={(e) => ziehe(Number(e.target.value))}
      />
      <span className={styles.wert}>{wert} %</span>
    </div>
  );
}
